/**
 * Privacy Store - Zustand State Management
 *
 * Manages the global state for sensitive data visibility toggle.
 * Persists user preference to localStorage for consistent experience across sessions.
 * Includes auto-hide on idle, print protection, and cross-tab sync.
 *
 * @example
 * const { isSensitiveHidden, toggleSensitiveData } = usePrivacyStore()
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

interface PrivacyState {
  /**
   * Whether sensitive information (email, phone, salary, etc.) is currently hidden
   */
  isSensitiveHidden: boolean

  /**
   * Whether the user has seen the onboarding tooltip
   */
  hasSeenOnboarding: boolean

  /**
   * Whether auto-hide on idle is enabled
   */
  autoHideOnIdle: boolean

  /**
   * Whether auto-hide on print is enabled
   */
  autoHideOnPrint: boolean

  /**
   * Last activity timestamp (for idle detection)
   */
  lastActivityTime: number

  /**
   * Toggle the visibility of sensitive data
   */
  toggleSensitiveData: () => void

  /**
   * Set the visibility state explicitly
   */
  setSensitiveData: (hidden: boolean) => void

  /**
   * Mark onboarding as seen
   */
  markOnboardingAsSeen: () => void

  /**
   * Update last activity time
   */
  updateActivity: () => void

  /**
   * Enable/disable auto-hide features
   */
  setAutoHideOnIdle: (enabled: boolean) => void
  setAutoHideOnPrint: (enabled: boolean) => void
}

export const usePrivacyStore = create<PrivacyState>()(
  persist(
    (set) => ({
      // Default: show all data (not hidden)
      isSensitiveHidden: false,
      hasSeenOnboarding: false,
      autoHideOnIdle: true, // Default: enabled
      autoHideOnPrint: true, // Default: enabled
      lastActivityTime: Date.now(),

      // Toggle function
      toggleSensitiveData: () =>
        set((state) => ({ isSensitiveHidden: !state.isSensitiveHidden })),

      // Explicit setter
      setSensitiveData: (hidden: boolean) =>
        set({ isSensitiveHidden: hidden }),

      // Mark onboarding as seen
      markOnboardingAsSeen: () =>
        set({ hasSeenOnboarding: true }),

      // Update activity timestamp
      updateActivity: () =>
        set({ lastActivityTime: Date.now() }),

      // Auto-hide toggles
      setAutoHideOnIdle: (enabled: boolean) =>
        set({ autoHideOnIdle: enabled }),

      setAutoHideOnPrint: (enabled: boolean) =>
        set({ autoHideOnPrint: enabled }),
    }),
    {
      name: 'monutrack-privacy-settings', // localStorage key
      storage: createJSONStorage(() => localStorage),
    }
  )
)

/**
 * Custom hook to easily access sensitive data state
 * @returns Boolean indicating if sensitive data should be hidden
 */
export const useSensitiveDataHidden = () => {
  return usePrivacyStore((state) => state.isSensitiveHidden)
}
